import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/yey6sygd_ritha%20barreto%20logo%20nova%202.png";

const navItems = [
  { label: 'Início', href: '#hero' },
  { label: 'Sobre', href: '#sobre' },
  { label: 'Cursos', href: '#cursos' },
  { label: 'Resultados', href: '#resultados' },
  { label: 'Depoimentos', href: '#depoimentos' },
  { label: 'Contato', href: '#contato' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        data-testid="navigation"
        className={`fixed top-3 left-3 right-3 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-black/80 backdrop-blur-xl border border-white/10'
            : 'bg-black/50 backdrop-blur-md border border-white/5'
        } rounded-full`}
        style={{ maxWidth: '1024px', margin: '0 auto' }}
      >
        <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-2 sm:py-3">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); scrollToSection('#hero'); }}
            className="flex-shrink-0"
            data-testid="nav-logo"
          >
            <img
              src={LOGO_URL}
              alt="Ritha Barreto Academy"
              className="h-7 sm:h-8 md:h-10 w-auto object-contain"
            />
          </a>

          {/* Desktop Menu - visible from xl breakpoint */}
          <div className="hidden xl:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="px-3 py-2 text-sm font-medium text-white/70 hover:text-gold transition-colors rounded-full hover:bg-white/5"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* CTA Button - visible from md */}
            <button
              onClick={() => scrollToSection('#contato')}
              className="hidden md:block bg-gold text-black font-semibold px-4 lg:px-6 py-2 lg:py-2.5 rounded-full text-xs lg:text-sm hover:bg-gold-light transition-all"
              data-testid="nav-cta"
            >
              Quero me Especializar
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
              aria-label="Abrir menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 sm:top-20 left-3 right-3 bg-dark-paper/95 backdrop-blur-xl border border-white/10 rounded-2xl z-40 p-4 xl:hidden"
            style={{ maxWidth: '400px', margin: '0 auto' }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="w-full text-left px-4 py-3 text-white/80 hover:text-gold hover:bg-white/5 rounded-xl transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('#contato')}
                className="w-full bg-gold text-black font-semibold px-4 py-3 rounded-xl mt-2 hover:bg-gold-light transition-all"
                data-testid="mobile-nav-cta"
              >
                Quero me Especializar
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
